/**
 * Conversations Service
 *
 * Frontend service for managing conversation history
 */

import { getFunctions } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { getEmulatorHost, isUsingEmulators } from './emulator';

export interface Conversation {
  id: string;
  title: string;
  createdAt: string | null;
  updatedAt: string | null;
  messageCount: number;
  preview: string;
  summary: string | null;
}

// In-memory cache for conversations list
let conversationsCache: { data: Conversation[]; timestamp: number } | null = null;
const CACHE_TTL_MS = 60_000; // 1 minute

export interface ConversationMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  model?: string;
  source?: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
  createdAt: string | null;
}

export interface ConversationDetail {
  conversation: {
    id: string;
    title: string;
    createdAt: string | null;
    updatedAt: string | null;
  };
  messages: ConversationMessage[];
}

/**
 * Invalidate the conversations cache.
 * Call after creating, deleting, or modifying conversations.
 */
export function invalidateConversationsCache(): void {
  conversationsCache = null;
}

/**
 * Fetch all conversations for the current user.
 * Returns cached data if available and fresh, fetches in background to refresh.
 */
export async function getConversations(options?: { skipCache?: boolean }): Promise<Conversation[]> {
  // Return cache immediately if fresh
  if (
    !options?.skipCache &&
    conversationsCache &&
    Date.now() - conversationsCache.timestamp < CACHE_TTL_MS
  ) {
    return conversationsCache.data;
  }

  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  // In emulator mode, force a refresh so we don't reuse a cached/prod token.
  const idToken = await user.getIdToken(isUsingEmulators());

  const functions = getFunctions();
  const projectId =
    functions.app.options.projectId ||
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    'postmortem-pheonix';
  const region = functions.region || 'us-central1';
  const baseUrl = isUsingEmulators()
    ? `http://${getEmulatorHost()}:${process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5001'}/${projectId}/${region}`
    : functions.customDomain || `https://${region}-${projectId}.cloudfunctions.net`;

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutMs = parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10);
  const timeoutId = setTimeout(() => controller.abort(), Number.isFinite(timeoutMs) ? timeoutMs : 30000);

  try {
    const response = await fetch(`${baseUrl}/getConversations`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const conversations = data.conversations || [];

    // Update cache
    conversationsCache = { data: conversations, timestamp: Date.now() };

    return conversations;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      console.warn('Conversations request timeout, returning empty list');
      return [];
    }
    throw error;
  }
}

/**
 * Fetch messages for a specific conversation
 */
export async function getConversationMessages(conversationId: string): Promise<ConversationDetail> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  // In emulator mode, force a refresh so we don't reuse a cached/prod token.
  const idToken = await user.getIdToken(isUsingEmulators());

  const functions = getFunctions();
  const projectId =
    functions.app.options.projectId ||
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    'postmortem-pheonix';
  const region = functions.region || 'us-central1';
  const baseUrl = isUsingEmulators()
    ? `http://${getEmulatorHost()}:${process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5001'}/${projectId}/${region}`
    : functions.customDomain || `https://${region}-${projectId}.cloudfunctions.net`;

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutMs = parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10);
  const timeoutId = setTimeout(() => controller.abort(), Number.isFinite(timeoutMs) ? timeoutMs : 30000);

  try {
    const response = await fetch(`${baseUrl}/getConversationMessages?conversationId=${conversationId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - conversation service not responding');
    }
    throw error;
  }
}

/**
 * Delete a conversation by ID
 */
export async function deleteConversation(conversationId: string): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  // In emulator mode, force a refresh so we don't reuse a cached/prod token.
  const idToken = await user.getIdToken(isUsingEmulators());

  const functions = getFunctions();
  const projectId =
    functions.app.options.projectId ||
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    'postmortem-pheonix';
  const region = functions.region || 'us-central1';
  const baseUrl = isUsingEmulators()
    ? `http://${getEmulatorHost()}:${process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5001'}/${projectId}/${region}`
    : functions.customDomain || `https://${region}-${projectId}.cloudfunctions.net`;

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutMs = parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10);
  const timeoutId = setTimeout(() => controller.abort(), Number.isFinite(timeoutMs) ? timeoutMs : 30000);

  try {
    const response = await fetch(`${baseUrl}/deleteConversation`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ conversationId }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - delete service not responding');
    }
    throw error;
  }
}

/**
 * Generate a summary for a conversation
 */
export async function generateConversationSummary(conversationId: string): Promise<string> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  // In emulator mode, force a refresh so we don't reuse a cached/prod token.
  const idToken = await user.getIdToken(isUsingEmulators());

  const functions = getFunctions();
  const projectId =
    functions.app.options.projectId ||
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    'postmortem-pheonix';
  const region = functions.region || 'us-central1';
  const baseUrl = isUsingEmulators()
    ? `http://${getEmulatorHost()}:${process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5001'}/${projectId}/${region}`
    : functions.customDomain || `https://${region}-${projectId}.cloudfunctions.net`;

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutMs = parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10);
  const timeoutId = setTimeout(() => controller.abort(), Number.isFinite(timeoutMs) ? timeoutMs : 30000);

  try {
    const response = await fetch(`${baseUrl}/generateConversationSummary`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ conversationId }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.summary;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - summary generation service not responding');
    }
    throw error;
  }
}

/**
 * Backfill summaries for existing conversations without summaries
 */
export async function backfillConversationSummaries(): Promise<{ message: string; results: any[] }> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  // In emulator mode, force a refresh so we don't reuse a cached/prod token.
  const idToken = await user.getIdToken(isUsingEmulators());

  const functions = getFunctions();
  const projectId =
    functions.app.options.projectId ||
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    'postmortem-pheonix';
  const region = functions.region || 'us-central1';
  const baseUrl = isUsingEmulators()
    ? `http://${getEmulatorHost()}:${process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5001'}/${projectId}/${region}`
    : functions.customDomain || `https://${region}-${projectId}.cloudfunctions.net`;

  // Create abort controller for timeout (longer timeout for backfill)
  const controller = new AbortController();
  const timeoutMs = 120000; // 2 minutes for backfill operation
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${baseUrl}/backfillConversationSummaries`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - backfill service not responding');
    }
    throw error;
  }
}
